import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Grid } from '@mui/material';
import { useDispatch } from "react-redux";
import { useCourse } from "../../course/hooks";
import Loader from "../../../components/Loader";
import { Course } from "./Course";
import ApprovalInProgress from "../../../components/study/course/ApprovalProgress";
import Outline from "../../../components/study/course/Outline";
import MicPermission from "../../../components/MicPermission";
import CoursePausedOverlay from "../../../components/study/course/CoursePausedOverlay";
import { CourseHeader } from "../../../components/study/course";
import { CourseComplete } from "../../../components/study/course";
import { CourseWelcome } from "./Welcome";
import { useStudy } from "../hooks";
import { sessionTimingAction } from "../../../store/sessionTiming/sessionTimingSlice";
import { addAssignmentActivitiesByCourseAndAssignee, getSessionGate } from "../../../store/study/studyBaseApi";


export const CourseTreeView = ({ courseId, status, summaries, activities }) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { loadCurrent, loadLessonPlan, current, lessonPlan, currentCourseComplete, setCurrentCourseCompleted, setAllCourseComplete, setNextCourse } = useCourse();
    const { profile } = useStudy();


    const [summaryData, setSummaryData] = useState([]);
    const [activityData, setActivityData] = useState([]);
    const [showOutline, setShowOutline] = useState(true);
    const [selectedCourse, setSelectedCourse] = useState(false); // Track selected course

    // Per-session gate (CourseGateDetail) — the authority for which sessions
    // are passed/failed/locked and for retake attempt counts. Refreshed after
    // every chapter completion by Sectionv2's advance verdict.
    const [gate, setGate] = useState(null);
    const refreshGate = useCallback(async () => {
        const _courseId = id || courseId;
        if (!_courseId || !profile?.id) return null;
        const resp = await getSessionGate({ courseId: _courseId, assigneeId: profile.id });
        const detail = resp?.data ?? null;
        setGate(detail);
        return detail;
    }, [id, courseId, profile?.id]);

    useEffect(() => {
        if (profile?.id && (id || courseId)) {
            refreshGate().catch((e) => console.error("sessionGate load failed", e));
        }
    }, [id, courseId, profile?.id]);

    useEffect(() => {
        let _id = id || courseId;
        if (_id) {

            loadCurrent({ id: _id });
            loadLessonPlan({ id: _id });
            setCurrentCourseCompleted(false);
            // Clear the write-once allCourseComplete flag and any stale
            // nextCourse from a previously viewed course, so the completion
            // decision in Welcome is made fresh against this course's own
            // /next-ir-level response instead of leftover state.
            setAllCourseComplete(false);
            setNextCourse({});
            setSelectedCourse(false);
        }
    }, [id, courseId]);

    // Start session timer when student enters the course (only if not already running)
    useEffect(() => {
        if (selectedCourse && current?.duration?.[0]?.sessionTime) {
            const existingEndTime = localStorage.getItem("sessionEndTime");
            const hasActiveSession = existingEndTime && Number(existingEndTime) > Date.now();
            if (!hasActiveSession) {
                const sessionMinutes = Number(current.duration[0].sessionTime);
                if (sessionMinutes > 0) {
                    const startTime = Date.now();
                    const endTime = startTime + sessionMinutes * 60 * 1000;
                    dispatch(sessionTimingAction.setSessionTime(sessionMinutes * 60));
                    dispatch(sessionTimingAction.startSessionTimer());

                    // Persist to backend as backup
                    if (courseId && profile?.id) {
                        addAssignmentActivitiesByCourseAndAssignee({
                            params: { courseId, assigneeId: profile.id },
                            body: { type: "SESSION_START", startTime: String(startTime), endTime: String(endTime) },
                        }).catch(() => {});
                    }
                }
            }
        }
    }, [selectedCourse, current]);

    // Clear session timer when the course is completed
    useEffect(() => {
        if (currentCourseComplete) {
            dispatch(sessionTimingAction.clearSessionTime());
        }
    }, [currentCourseComplete]);

    useEffect(() => {
        setSummaryData(summaries);
    }, [summaries]);

    useEffect(() => {
        if (activities && activities.length) {
            setActivityData(activities);
        }
    }, [activities]);

    if (!Object.keys(lessonPlan).length || !Object.keys(current).length) {
        return <Loader />;
    }

    if (currentCourseComplete) {
        return <CourseComplete course={current} />
    }

    if (!selectedCourse) {
        return (<CourseWelcome student={profile} setSelectedCourse={setSelectedCourse} courseId={courseId} activities={activities} summaries={summaries} gate={gate} />
        );
    } else {

        return (
            <React.Fragment>
                <CourseHeader course={current} />

                <Grid container spacing={2}>
                    {showOutline && <Grid item xs={12} md={3} sx={{ mt: 1 }}>
                        {lessonPlan && Object.keys(lessonPlan).length > 0 && <Outline
                            lessonPlan={lessonPlan}
                            courseId={courseId}
                            gate={gate}
                        />}
                    </Grid>}
                    <Grid item xs={12} md={9} sx={{ position: 'relative', mt: 1, mb: 8 }}>
                        {/* {current && status != 'ASSIGNED' ? <Course */}
                        <CoursePausedOverlay />
                        {current ? <Course
                            course={current}
                            setShowCourseOutline={setShowOutline}
                            summaries={summaryData}
                            activities={activityData}
                            lessonPlan={lessonPlan}
                            gate={gate}
                            refreshGate={refreshGate}
                        /> : <ApprovalInProgress course={current} />}
                    </Grid>
                    <MicPermission context="course" />
                </Grid>
            </React.Fragment>
        );
    }
};